import axios, { AxiosResponse, AxiosError } from 'axios';
import { ThirtyOneError, ErrorCode } from '../Error/ThirtyOneError';
import Logger from '../Logger/Logger';
import {userRepository} from "../Repositories/UserRepository";

const logger = Logger.Create('HttpService');
const requestTimeout = 5000;

/**
 * Handles the HTTP operations.
 */
export default class HttpService
{
  /**
   * Make a GET request to the server without a token.
   */
  public static async tokenlessGet<T>(url: string): Promise<T>
  {
    logger.info(`Sending request: ${url}`);

    const response = await HttpService
      .performRequest(() => axios.get(url, {
        timeout: requestTimeout,
        headers: this.createHeader(),
      }));

    return this.handleResponse(response);
  }

  /**
   * Make a tokenless POST request to the server
   * @param url URL to send the request to
   * @param body Request body
   */
  public static async tokenlessPost<T>(url: string, body?: object, params?: object): Promise<T>
  {
    logger.info(`Sending request: ${url}`);

    const response = await HttpService
        .performRequest(() => axios.post(
            url,
            body,
            {
              timeout: requestTimeout,
              params,
            },
        ));

    return this.handleResponse(response);
  }
  
  /**
   * Make a POST request to the server
   * @param url URL to send the request to
   * @param body Request body
   */
  public static async post<T>(url: string, body?: object, params?: object): Promise<T>
  {
    logger.info(`Sending request: ${url}`);

    const response = await HttpService
      .performRequest(() => axios.post(
        url,
        body,
        {
          timeout: requestTimeout,
          params,
          headers: this.createHeader(),
        },
      ));

    return this.handleResponse(response);
  }

  /**
   * Make a PUT request to the server
   * @param url URL to send the request to
   * @param body Request body
   */
  public static async put<T>(url: string, body?: object, params?: object): Promise<T>
  {
    logger.info(`Sending PUT request: ${url}`);

    const response = await HttpService
        .performRequest(() => axios.put(
            url,
            body,
            {
              timeout: requestTimeout,
              params,
              headers: this.createHeader(),
            },
        ));

    return this.handleResponse(response);
  }
  
  private static async handleResponse<T>(response: AxiosResponse): Promise<T>
  {
    if (response.status === 400)
    {
      return Promise.reject(new ThirtyOneError(
        ErrorCode.BadRequest, JSON.stringify(response.data),
      ));
    }

    if (response.status === 401)
    {
      return Promise.reject(new ThirtyOneError(
          ErrorCode.ExpiredToken, JSON.stringify(response.data),
      ));
    }

    if (response.status === 500)
    {
      return Promise.reject(new ThirtyOneError(
        ErrorCode.ServerError, JSON.stringify(response.data),
      ));
    }

    if (response.status === 404)
    {
      return Promise.reject(new ThirtyOneError(
        ErrorCode.NotFound, response.data,
      ));
    }

    // Failed dependency: Used when the user cannot be found.
    if (response.status === 424)
    {
      return Promise.reject(new ThirtyOneError(
        ErrorCode.UnknownUser, response.data,
      ));
    }

    // Maximum Pin attempt reached.
    if (response.status === 444)
    {
      return Promise.reject(new ThirtyOneError(
        ErrorCode.MaximumAttemptReached, response.data,
      ));
    }


    if (response.status !== 200)
    {
      return Promise.reject(new ThirtyOneError(
        ErrorCode.RequestError, JSON.stringify(response.data),
      ));
    }

    return response.data;
  }

  /**
   * Creates and returns a header using user token.
   */
  private static createHeader(): Record<string, string>
  {
    const header: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${userRepository.token}`,
    };

    return header;
  }

  /**
   * Performs network request.
   * @param request Request packet.
   */
  private static async performRequest(request: () => Promise<AxiosResponse>): Promise<AxiosResponse>
  {
    // Attempt request
    const response = await request()
      .catch((error: AxiosError) =>
      {
        if (error.response === undefined)
        {
          return Promise.reject(new ThirtyOneError(ErrorCode.RequestError, error.message));
        }

        return error.response;
      });

    return response;
  }
}

export const httpService = new HttpService();
